import { notificationPollingQueryOptions } from "@istari-frontend/notifications/data-source";
import { type ActorLookup, type Notification, shortId } from "@istari-frontend/notifications/model";
import { bucketWorkflowLogStatus } from "@istari-frontend/workflow-log/model";
import type { V3 } from "@istari/istari-client";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import type { ChangeRequestId } from "~/change-request/model/change-request";

import { UNKNOWN_ACTOR_LOOKUP } from "./actor-lookup";
import {
  hasEventMetadata,
  type ReceivedResourceEventData,
  toJobStatus,
  toReceivedResourcePayload,
  toSharedResourceType,
  toSharedRole,
  type WithEventMetadata,
} from "./inbox-adapter";
import { type ChangeRequestDetail, useChangeRequestEnrichment } from "./use-change-request-enrichment";
import { type MissingEventMetadata, useReportMissingEventMetadata } from "./use-report-missing-event-metadata";
import {
  type UnrecognizedNotificationEvent,
  useReportUnrecognizedNotificationEvents,
} from "./use-report-unrecognized-events";
import {
  type UnrecognizedWorkflowLogStatus,
  useReportUnrecognizedWorkflowLogStatuses,
} from "./use-report-unrecognized-workflow-log-statuses";
import { useShareEnrichment } from "./use-share-enrichment";
import { useWorkflowLogEnrichment, type WorkflowLogDetail } from "./use-workflow-log-enrichment";

export function useNotificationPolling(options?: { enabled?: boolean }) {
  const pollingQuery = useQuery({
    ...notificationPollingQueryOptions.list(),
    enabled: options?.enabled ?? true,
  });

  const shareEvents = useMemo(
    () =>
      (pollingQuery.data?.items ?? [])
        .filter((item) => item.data.eventType === "notification.user.access.granted")
        .map((item) => item.data as V3.UserAccessGrantedNotificationEvent),
    [pollingQuery.data],
  );

  const resourceIds = useMemo(() => shareEvents.map((event) => event.resourceId), [shareEvents]);
  const { resourceDetails, actorNames } = useShareEnrichment(resourceIds);

  const changeRequestEvents = (pollingQuery.data?.items ?? [])
    .filter((item) => item.data.eventType === "notification.user.change_request.opened")
    .map((item) => item.data as V3.SystemChangeRequestOpenedNotificationEvent);

  const { changeRequestDetails, requesterNames } = useChangeRequestEnrichment(changeRequestEvents);

  const workflowLogEvents = (pollingQuery.data?.items ?? [])
    .filter((item) => item.data.eventType === "notification.user.workflow_log.completed")
    .map((item) => item.data as V3.UserWorkflowLogCompletedNotificationEvent);

  const { workflowLogDetails, workflowLogCreatorNames, workflowLogSystemNames } =
    useWorkflowLogEnrichment(workflowLogEvents);

  const built = useMemo(
    () =>
      buildNotifications(pollingQuery.data?.items ?? [], {
        actorNames,
        resourceDetails,
        requesterNames,
        changeRequestDetails,
        workflowLogCreatorNames,
        workflowLogDetails,
        workflowLogSystemNames,
      }),
    [
      pollingQuery.data,
      actorNames,
      resourceDetails,
      requesterNames,
      changeRequestDetails,
      workflowLogCreatorNames,
      workflowLogDetails,
      workflowLogSystemNames,
    ],
  );

  useReportUnrecognizedNotificationEvents(built.unrecognized);
  useReportUnrecognizedWorkflowLogStatuses(built.unrecognizedWorkflowLogStatuses);
  useReportMissingEventMetadata(built.missingMetadata);

  return { ...pollingQuery, data: pollingQuery.data ? built.notifications : undefined };
}

interface PollingEnrichment {
  actorNames: ActorLookup[];
  resourceDetails: { name: string | null; resourceType: string | null; createdById: string | null }[];
  requesterNames: ActorLookup[];
  changeRequestDetails: ChangeRequestDetail[];
  workflowLogCreatorNames: ActorLookup[];
  workflowLogDetails: WorkflowLogDetail[];
  workflowLogSystemNames: (string | null)[];
}

function buildNotifications(
  rawItems: V3.NotificationDto[],
  enrichment: PollingEnrichment,
): {
  notifications: Notification[];
  unrecognized: UnrecognizedNotificationEvent[];
  unrecognizedWorkflowLogStatuses: UnrecognizedWorkflowLogStatus[];
  missingMetadata: MissingEventMetadata[];
} {
  const notifications: Notification[] = [];
  const unrecognized: UnrecognizedNotificationEvent[] = [];
  const unrecognizedWorkflowLogStatuses: UnrecognizedWorkflowLogStatus[] = [];
  const missingMetadata: MissingEventMetadata[] = [];
  let shareIdx = 0;
  let changeRequestIdx = 0;
  let workflowLogIdx = 0;

  for (const item of rawItems) {
    const { data } = item;

    switch (data.eventType) {
      case "notification.user.access.granted": {
        const actorName = enrichment.actorNames[shareIdx] ?? UNKNOWN_ACTOR_LOOKUP;
        const resource = enrichment.resourceDetails[shareIdx] ?? null;
        shareIdx++;
        if (!hasEventMetadata(data)) {
          missingMetadata.push({ eventType: "notification.user.access.granted", itemId: data.resourceId });
          break;
        }
        notifications.push(toShareNotification(data, actorName, resource));
        break;
      }
      case "notification.user.job.terminal_state": {
        if (!hasEventMetadata(data)) {
          missingMetadata.push({ eventType: "notification.user.job.terminal_state", itemId: data.jobId });
          break;
        }
        const notification = toJobNotification(data);
        if (notification) notifications.push(notification);
        break;
      }
      case "notification.user.received_resource.shared":
      case "notification.user.received_resource.updated":
      case "notification.user.received_resource.revoked": {
        const notification = toReceivedResourceNotification(data);
        if (notification) notifications.push(notification);
        break;
      }
      case "notification.user.change_request.opened": {
        const requesterName = enrichment.requesterNames[changeRequestIdx] ?? UNKNOWN_ACTOR_LOOKUP;
        const changeRequest = enrichment.changeRequestDetails[changeRequestIdx] ?? null;
        changeRequestIdx++;
        if (!hasEventMetadata(data)) {
          missingMetadata.push({ eventType: "notification.user.change_request.opened", itemId: data.changeRequestId });
          break;
        }
        notifications.push(toPolledChangeRequestNotification(data, requesterName, changeRequest));
        break;
      }
      case "notification.user.workflow_log.completed": {
        const index = workflowLogIdx;
        workflowLogIdx++;
        if (!hasEventMetadata(data)) {
          missingMetadata.push({
            eventType: "notification.user.workflow_log.completed",
            itemId: data.workflowLogEntryId,
          });
          break;
        }
        const { notification, unrecognizedStatus } = buildPolledWorkflowLog(data, enrichment, index);
        notifications.push(notification);
        if (unrecognizedStatus) unrecognizedWorkflowLogStatuses.push(unrecognizedStatus);
        break;
      }
      default: {
        const _exhaustive: never = data;
        unrecognized.push({ eventType: item.eventType });
      }
    }
  }

  return { notifications, unrecognized, unrecognizedWorkflowLogStatuses, missingMetadata };
}

function buildPolledWorkflowLog(
  data: WithEventMetadata<V3.UserWorkflowLogCompletedNotificationEvent>,
  enrichment: PollingEnrichment,
  index: number,
): { notification: Notification; unrecognizedStatus: UnrecognizedWorkflowLogStatus | null } {
  const notification = toPolledWorkflowLogNotification(
    data,
    enrichment.workflowLogCreatorNames[index] ?? UNKNOWN_ACTOR_LOOKUP,
    enrichment.workflowLogDetails[index] ?? null,
    enrichment.workflowLogSystemNames[index] ?? null,
  );
  const unrecognizedStatus =
    bucketWorkflowLogStatus(data.status) === "unspecified"
      ? { status: data.status, workflowLogEntryId: data.workflowLogEntryId }
      : null;
  return { notification, unrecognizedStatus };
}

function toReceivedResourceNotification(data: ReceivedResourceEventData): Notification | null {
  const payload = toReceivedResourcePayload(data);
  if (!payload) return null;

  return {
    id: data.eventId ?? `${data.eventType}:${data.resourceId}:${data.occurredAt.toISOString()}`,
    type: "received-resource",
    createdAt: data.createdAt ?? data.occurredAt,
    status: "unread",
    payload,
  };
}

function toShareNotification(
  data: WithEventMetadata<V3.UserAccessGrantedNotificationEvent>,
  actorName: ActorLookup,
  resource: { name: string | null; resourceType: string | null; createdById: string | null } | null,
): Notification {
  const actorId = resource?.createdById ?? data.grantedUserId;

  return {
    id: `${data.grantedUserId}:${data.resourceId}:${data.eventId}`,
    type: "share",
    createdAt: data.createdAt,
    status: "unread",
    triggeredBy: { ...actorName, userId: actorId },
    payload: {
      resourceType: toSharedResourceType(resource?.resourceType) ?? "file",
      resourceId: data.resourceId,
      resourceName: resource?.name ?? data.resourceId,
      role: toSharedRole(data.permissionLevel),
      shareCount: 1,
    },
  };
}

function toPolledChangeRequestNotification(
  data: WithEventMetadata<V3.SystemChangeRequestOpenedNotificationEvent>,
  requesterName: ActorLookup,
  changeRequest: ChangeRequestDetail | null,
): Notification {
  return {
    id: `change-request:${data.changeRequestId}:${data.eventId}`,
    type: "changeRequest",
    createdAt: data.createdAt,
    status: "unread",
    triggeredBy: { ...requesterName, userId: changeRequest?.createdById ?? null },
    payload: {
      changeRequestId: data.changeRequestId as ChangeRequestId,
      systemId: data.systemId,
      title: changeRequest?.title ?? shortId(data.changeRequestId),
    },
  };
}

function toPolledWorkflowLogNotification(
  data: WithEventMetadata<V3.UserWorkflowLogCompletedNotificationEvent>,
  creatorName: ActorLookup,
  entry: WorkflowLogDetail | null,
  systemName: string | null,
): Notification {
  return {
    id: `workflow-log:${data.workflowLogEntryId}:${data.eventId}`,
    type: "workflowLog",
    createdAt: data.createdAt,
    status: "unread",
    triggeredBy: { ...creatorName, userId: entry?.createdById ?? null },
    payload: {
      workflowLogEntryId: data.workflowLogEntryId,
      systemId: data.systemId,
      systemName,
      status: data.status,
      outcome: bucketWorkflowLogStatus(data.status),
      title: entry?.title ?? shortId(data.workflowLogEntryId),
    },
  };
}

function toJobNotification(data: WithEventMetadata<V3.UserJobTerminalStateNotificationEvent>): Notification | null {
  const jobStatus = toJobStatus(data.terminalState);
  if (!jobStatus) return null;

  return {
    id: `job:${data.jobId}:${data.eventId}`,
    type: "job",
    createdAt: data.createdAt,
    status: "unread",
    triggeredBy: { status: "resolved", userId: "system", displayName: "Build Pipeline" },
    payload: {
      jobId: data.jobId,
      jobStatus,
      functionName: `Job ${data.jobId.slice(0, 8)}`,
    },
  };
}
